// Pure diff engine for AI regeneration proposals (plan §7) — no UI imports.
//
// `buildRiskDiff` compares the persisted draft (current) against a regeneration
// proposal (proposed) section by section. `mergeRiskDiff` applies the user's
// explicit per-section keep/take decisions and THROWS if any changed section is
// undecided — decision-completeness is enforced in the flow itself, not just in
// the UI (Mental Model: nothing is accepted unseen).

import { RiskAssessment } from "components/Risk/types";
import { calculateScore } from "components/Risk/utils";

import {
  AIDraftMitigation,
  AIDraftRisk,
  DiffDecision,
  RiskDiffSection,
} from "./types";

export const BASICS_FIELDS = [
  "readable_name",
  "description",
  "protection_goal",
  "personal",
] as const;

export const CLASSIFICATION_FIELDS = [
  "group_id",
  "origin_id",
  "consequence_id",
  "phase_id",
] as const;

export const ASSESSMENT_PARAMS = [
  "impact",
  "people_count",
  "duration",
  "detectability",
  "probability",
] as const;

// `id` is re-minted by the backend on every generation and `version` is server
// bookkeeping — comparing them would flag content-identical mitigations as
// changed after every regeneration.
export const MITIGATION_COMPARE_FIELDS = [
  "type",
  "description",
  "pictogram",
  "left_risk",
  "reasoning",
  "applied_standards",
  "impact",
  "people_count",
  "duration",
  "detectability",
  "probability",
] as const;

const pick = <T, K extends keyof T>(obj: T, keys: readonly K[]): Pick<T, K> => {
  const result = {} as Pick<T, K>;
  keys.forEach((key) => {
    result[key] = obj[key];
  });
  return result;
};

// Hand-rolled deep equality for the plain-JSON payloads compared here
// (primitives, arrays, plain objects). Array comparison is order-sensitive on
// purpose: over-reporting a change is safer than under-reporting one.
const isEqual = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;

  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    return (
      a.length === b.length && a.every((item, index) => isEqual(item, b[index]))
    );
  }

  if (a && b && typeof a === "object" && typeof b === "object") {
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    return (
      aKeys.length === bKeys.length &&
      aKeys.every((key) =>
        isEqual(
          (a as Record<string, unknown>)[key],
          (b as Record<string, unknown>)[key]
        )
      )
    );
  }

  return false;
};

const cloneMitigation = (mitigation: AIDraftMitigation): AIDraftMitigation => ({
  ...mitigation,
  applied_standards: [...(mitigation.applied_standards ?? [])],
});

const cloneDraft = (draft: AIDraftRisk): AIDraftRisk => ({
  ...draft,
  assessment: { ...draft.assessment },
  mitigations: draft.mitigations.map(cloneMitigation),
});

// Optional strings are normalized to "" so `undefined` vs "" never shows up as
// a phantom change.
const basicsPayload = (draft: AIDraftRisk) => ({
  readable_name: draft.readable_name ?? "",
  description: draft.description,
  protection_goal: draft.protection_goal,
  personal: draft.personal,
});

const classificationPayload = (draft: AIDraftRisk) =>
  pick(draft, CLASSIFICATION_FIELDS);

const assessmentPayload = (draft: AIDraftRisk) => {
  const params: RiskAssessment = pick(draft, ASSESSMENT_PARAMS);
  // `score` is derived from the 5 params (display convenience for the diff UI);
  // it never affects change detection on its own.
  return { ...params, score: calculateScore(params) };
};

const reasoningPayload = (draft: AIDraftRisk) => ({
  reasoning: draft.reasoning ?? "",
});

const mitigationComparePayload = (mitigation: AIDraftMitigation) => ({
  ...pick(mitigation, MITIGATION_COMPARE_FIELDS),
  applied_standards: mitigation.applied_standards ?? [],
});

export const buildRiskDiff = (
  current: AIDraftRisk,
  proposed: AIDraftRisk
): RiskDiffSection[] => {
  const sections: RiskDiffSection[] = [];

  const currentBasics = basicsPayload(current);
  const proposedBasics = basicsPayload(proposed);
  sections.push({
    key: "basics",
    kind: "basics",
    changed: !isEqual(currentBasics, proposedBasics),
    current: currentBasics,
    proposed: proposedBasics,
  });

  const currentClassification = classificationPayload(current);
  const proposedClassification = classificationPayload(proposed);
  sections.push({
    key: "classification",
    kind: "classification",
    changed: !isEqual(currentClassification, proposedClassification),
    current: currentClassification,
    proposed: proposedClassification,
  });

  const currentAssessment = assessmentPayload(current);
  const proposedAssessment = assessmentPayload(proposed);
  sections.push({
    key: "assessment",
    kind: "assessment",
    changed: !isEqual(currentAssessment, proposedAssessment),
    current: currentAssessment,
    proposed: proposedAssessment,
  });

  // One section per mitigation index present in EITHER version, pairwise by
  // index — extras on the proposed side are additions, extras on the current
  // side are removals. Every added/removed mitigation is a changed section by
  // definition (it requires an explicit decision).
  const mitigationCount = Math.max(
    current.mitigations.length,
    proposed.mitigations.length
  );
  for (let index = 0; index < mitigationCount; index++) {
    const currentMitigation = current.mitigations[index];
    const proposedMitigation = proposed.mitigations[index];

    if (currentMitigation && proposedMitigation) {
      sections.push({
        key: `mitigation:${index}`,
        kind: "mitigation",
        mitigationIndex: index,
        changed: !isEqual(
          mitigationComparePayload(currentMitigation),
          mitigationComparePayload(proposedMitigation)
        ),
        current: cloneMitigation(currentMitigation),
        proposed: cloneMitigation(proposedMitigation),
      });
    } else if (proposedMitigation) {
      sections.push({
        key: `mitigation:${index}`,
        kind: "mitigation_added",
        mitigationIndex: index,
        changed: true,
        current: null,
        proposed: cloneMitigation(proposedMitigation),
      });
    } else {
      sections.push({
        key: `mitigation:${index}`,
        kind: "mitigation_removed",
        mitigationIndex: index,
        changed: true,
        current: cloneMitigation(currentMitigation),
        proposed: null,
      });
    }
  }

  const currentReasoning = reasoningPayload(current);
  const proposedReasoning = reasoningPayload(proposed);
  sections.push({
    key: "reasoning",
    kind: "reasoning",
    changed: !isEqual(currentReasoning, proposedReasoning),
    current: currentReasoning,
    proposed: proposedReasoning,
  });

  return sections;
};

const assignFields = <K extends keyof AIDraftRisk>(
  target: AIDraftRisk,
  source: AIDraftRisk,
  keys: readonly K[]
) => {
  keys.forEach((key) => {
    target[key] = source[key];
  });
};

// Applies the per-section decisions to a fresh copy of `current` (inputs are
// never mutated). Decisions on UNCHANGED sections are ignored: content-wise
// keep === take there, and keeping the current side preserves stable
// backend-minted mitigation ids. `fully_mitigated` is deliberately part of NO
// section — the user decides it in the Accept Gate, never the AI (§1.2.9).
export const mergeRiskDiff = (
  current: AIDraftRisk,
  proposed: AIDraftRisk,
  decisions: Record<string, DiffDecision>
): AIDraftRisk => {
  const sections = buildRiskDiff(current, proposed);

  const undecided = sections
    .filter(
      (section) =>
        section.changed &&
        decisions[section.key] !== "keep" &&
        decisions[section.key] !== "take"
    )
    .map((section) => section.key);
  if (undecided.length > 0) {
    throw new Error(
      `mergeRiskDiff: undecided changed sections: ${undecided.join(", ")}`
    );
  }

  const takes = (key: string) => {
    const section = sections.find((s) => s.key === key);
    return Boolean(section?.changed) && decisions[key] === "take";
  };

  const merged = cloneDraft(current);

  if (takes("basics")) {
    assignFields(merged, proposed, BASICS_FIELDS);
  }
  if (takes("classification")) {
    assignFields(merged, proposed, CLASSIFICATION_FIELDS);
  }
  if (takes("assessment")) {
    assignFields(merged, proposed, ASSESSMENT_PARAMS);
    // Keep the duplicated `assessment` mirror consistent with the top-level
    // params (mirrors the useUpdateRisk duplication convention).
    merged.assessment = pick(proposed, ASSESSMENT_PARAMS);
  }
  if (takes("reasoning")) {
    merged.reasoning = proposed.reasoning;
  }

  const mitigationCount = Math.max(
    current.mitigations.length,
    proposed.mitigations.length
  );
  const mitigations: AIDraftMitigation[] = [];
  for (let index = 0; index < mitigationCount; index++) {
    const currentMitigation = current.mitigations[index];
    const proposedMitigation = proposed.mitigations[index];
    const taken = takes(`mitigation:${index}`);

    if (currentMitigation && proposedMitigation) {
      // Changed pair + "take" → proposal; otherwise (kept or identical) the
      // current mitigation stays, preserving its id.
      mitigations.push(
        cloneMitigation(taken ? proposedMitigation : currentMitigation)
      );
    } else if (proposedMitigation) {
      // mitigation_added: "take" adds it, "keep" rejects the addition.
      if (taken) {
        mitigations.push(cloneMitigation(proposedMitigation));
      }
    } else if (currentMitigation) {
      // mitigation_removed: "take" accepts the removal, "keep" retains it.
      if (!taken) {
        mitigations.push(cloneMitigation(currentMitigation));
      }
    }
  }
  merged.mitigations = mitigations;

  return merged;
};
